<template>
  <div class="text-config-container">
    <div class="text-config-content">
      <div class="text-config-header">
        <h2>文案创作设置</h2>
        <p>配置文案创作模块的参数和模板</p>
      </div>

      <div class="config-section">
        <h3>文案类型配置</h3>
        <div class="config-grid">
          <div class="config-item">
            <label>通用文案</label>
            <el-switch
              :value="config.general ? config.general.enabled : true"
              @change="val => { if(config.general) config.general.enabled = val }"
            ></el-switch>
            <input
              type="text"
              :value="config.general ? config.general.prompt : ''"
              @input="e => { if(config.general) config.general.prompt = e.target.value }"
              placeholder="通用文案提示词模板"
              class="prompt-input"
            >
          </div>
          <div class="config-item">
            <label>电商营销</label>
            <el-switch
              :value="config.ecommerce ? config.ecommerce.enabled : true"
              @change="val => { if(config.ecommerce) config.ecommerce.enabled = val }"
            ></el-switch>
            <input
              type="text"
              :value="config.ecommerce ? config.ecommerce.prompt : ''"
              @input="e => { if(config.ecommerce) config.ecommerce.prompt = e.target.value }"
              placeholder="电商营销文案提示词模板"
              class="prompt-input"
            >
          </div>
          <div class="config-item">
            <label>项目方案</label>
            <el-switch
              :value="config.project ? config.project.enabled : true"
              @change="val => { if(config.project) config.project.enabled = val }"
            ></el-switch>
            <input
              type="text"
              :value="config.project ? config.project.prompt : ''"
              @input="e => { if(config.project) config.project.prompt = e.target.value }"
              placeholder="项目方案文案提示词模板"
              class="prompt-input"
            >
          </div>
          <div class="config-item">
            <label>文案改进</label>
            <el-switch
              :value="config.improve ? config.improve.enabled : true"
              @change="val => { if(config.improve) config.improve.enabled = val }"
            ></el-switch>
            <input
              type="text"
              :value="config.improve ? config.improve.prompt : ''"
              @input="e => { if(config.improve) config.improve.prompt = e.target.value }"
              placeholder="文案改进提示词模板"
              class="prompt-input"
            >
          </div>
          <div class="config-item">
            <label>总结报告</label>
            <el-switch
              :value="config.summary ? config.summary.enabled : true"
              @change="val => { if(config.summary) config.summary.enabled = val }"
            ></el-switch>
            <input
              type="text"
              :value="config.summary ? config.summary.prompt : ''"
              @input="e => { if(config.summary) config.summary.prompt = e.target.value }"
              placeholder="总结报告文案提示词模板"
              class="prompt-input"
            >
          </div>
        </div>
      </div>

      <div class="config-section">
        <h3>字数限制</h3>
        <div class="length-config">
          <el-checkbox-group v-model="selectedLengthOptions">
            <el-checkbox
              v-for="option in lengthOptions"
              :key="option.key"
              :label="option.key"
              class="length-checkbox"
            >
              {{ option.label }}
            </el-checkbox>
          </el-checkbox-group>
        </div>
      </div>

      <div class="config-section">
        <h3>风格配置</h3>
        <div class="style-tags">
          <div v-for="(style, key) in config.styles" :key="key" class="style-tag" :class="{ 'enabled': style.enabled }">
            <span class="style-name">{{ style.name }}</span>
            <span v-if="style.description" class="style-desc">- {{ style.description }}</span>
            <el-button 
              type="text" 
              size="small" 
              @click="toggleStyle(key, !style.enabled)" 
              class="toggle-btn"
            >
              <i v-if="style.enabled" class="el-icon-check" style="color: #67c23a;"></i>
              <i v-else class="el-icon-close" style="color: #f56c6c;"></i>
            </el-button>
            <el-button 
              type="text" 
              size="small" 
              @click="removeStyle(key)" 
              class="remove-btn"
            >
              <i class="el-icon-close"></i>
            </el-button>
          </div>
          <el-button 
            type="primary" 
            size="small" 
            @click="openAddStyleDialog" 
            class="add-style-btn"
          >
            添加风格
          </el-button>
        </div>
      </div>

      <div class="config-section">
        <h3>模型配置</h3>
        <div class="model-config-grid">
          <div class="config-item">
            <label>默认模型</label>
            <el-select v-model="config.model" class="select" placeholder="选择默认模型" clearable>
              <el-option v-for="model in textModels" :key="model.identifier" :label="model.name" :value="model.identifier">
                <div class="model-option">
                  <img v-if="model.image_url" :src="model.image_url" alt="模型图片" class="model-icon">
                  <span class="model-name">{{ model.name }}</span>
                  <el-tag v-if="model.is_hot" size="small" type="danger" class="model-tag">热门</el-tag>
                  <el-tag v-if="model.is_new" size="small" type="primary" class="model-tag">新品</el-tag>
                </div>
              </el-option>
            </el-select>
          </div>
          <div class="config-item">
            <label>模型温度</label>
            <el-slider :value="config.temperature" @change="val => config.temperature = val" :min="0" :max="1" :step="0.1"></el-slider>
          </div>
          <div class="config-item">
            <label>最大生成长度</label>
            <el-input-number :value="config.maxTokens" @change="val => config.maxTokens = val" :min="100" :max="2000" class="number-input"></el-input-number>
            <span class="unit">tokens</span>
          </div>
        </div>
      </div>

      <div class="actions">
        <el-button type="primary" @click="saveConfig">保存设置</el-button>
        <el-button @click="resetConfig">重置</el-button>
      </div>
    </div>

    <!-- 添加风格对话框 -->
    <el-dialog title="添加风格" :visible.sync="addStyleDialogVisible" width="500px">
      <el-form :model="newStyle" label-width="80px">
        <el-form-item label="风格名称">
          <el-input v-model="newStyle.name" placeholder="请输入风格名称" />
        </el-form-item>
        <el-form-item label="风格描述">
          <el-input
            v-model="newStyle.description"
            type="textarea"
            placeholder="请输入风格描述"
            :rows="3"
          />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="newStyle.enabled" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="addStyleDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="addStyle">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import appCache from '../../utils/appCache'

export default {
  name: 'TextConfig',
  data() {
    return {
      config: {
        general: { enabled: true, prompt: '' },
        ecommerce: { enabled: true, prompt: '' },
        project: { enabled: true, prompt: '' },
        improve: { enabled: true, prompt: '' },
        summary: { enabled: true, prompt: '' },
        styles: {},
        lengthOptions: [],
        model: '',
        temperature: 0.7,
        maxTokens: 1000
      },
      lengthOptions: [
        { key: 'short', label: '短文案（50~100）', min: 50, max: 100 },
        { key: 'medium', label: '中文案（100~500）', min: 100, max: 500 },
        { key: 'long', label: '长文案（500以上）', min: 500, max: 9999 }
      ],
      selectedLengthOptions: ['short', 'medium', 'long'],
      selectedStyles: [],
      addStyleDialogVisible: false,
      newStyle: {
        name: '',
        description: '',
        enabled: true
      },
      textModels: [],
      loadingModels: false
    }
  },
  methods: {
    toggleStyle(key, val) {
      // 更新selectedStyles数组，保持与style.enabled状态一致
      if (val && !this.selectedStyles.includes(key)) {
        this.selectedStyles.push(key)
      } else if (!val) {
        this.selectedStyles = this.selectedStyles.filter(s => s !== key)
      }
    },
    async loadConfig() {
      try {
        const response = await axios.get('/admin/config/text')
        if (response.data.code === 200) {
          const data = response.data.data
          // 合并数据，确保所有必要字段都存在
          this.config.general = { ...{ enabled: true, prompt: '' }, ...data.general }
          this.config.ecommerce = { ...{ enabled: true, prompt: '' }, ...data.ecommerce }
          this.config.project = { ...{ enabled: true, prompt: '' }, ...data.project }
          this.config.improve = { ...{ enabled: true, prompt: '' }, ...data.improve }
          this.config.summary = { ...{ enabled: true, prompt: '' }, ...data.summary }
          this.config.styles = data.styles || {}
          this.config.model = data.model !== undefined ? data.model : ''
          this.config.temperature = data.temperature !== undefined ? data.temperature : 0.7
          this.config.maxTokens = data.maxTokens !== undefined ? data.maxTokens : 1000
          this.config.lengthOptions = data.lengthOptions || this.lengthOptions

          // 处理选中状态
          this.selectedStyles = Object.keys(this.config.styles).filter(key => this.config.styles[key]?.enabled)
          this.selectedLengthOptions = this.config.lengthOptions.map(opt => opt.key)
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        this.$message.error('加载配置失败')
      }
    },
    async loadModels() {
      this.loadingModels = true
      try {
        const textModels = await appCache.getModelsByCategory('text')
        this.textModels = textModels
      } catch (error) {
        console.error('加载模型失败:', error)
      } finally {
        this.loadingModels = false
      }
    },
    async saveConfig() {
      try {
        // 构建保存数据
        const configToSave = {
          general: this.config.general,
          ecommerce: this.config.ecommerce,
          project: this.config.project,
          improve: this.config.improve,
          summary: this.config.summary,
          styles: {},
          lengthOptions: this.lengthOptions.map(opt => ({
            ...opt,
            enabled: this.selectedLengthOptions.includes(opt.key)
          })),
          model: this.config.model,
          temperature: this.config.temperature,
          maxTokens: this.config.maxTokens
        }

        // 处理风格选中状态
        Object.keys(this.config.styles).forEach(key => {
          configToSave.styles[key] = {
            ...this.config.styles[key],
            enabled: this.selectedStyles.includes(key)
          }
        })

        const response = await axios.post('/admin/config/text', configToSave)
        if (response.data.code === 200) {
          this.$message.success('保存成功')
          // 刷新缓存，确保前端能拿到最新配置
          await appCache.refreshCache()
        } else {
          this.$message.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error('保存配置失败:', error)
        this.$message.error('保存配置失败')
      }
    },
    resetConfig() {
      this.$confirm('确定要重置所有配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.config = {
          general: { enabled: true, prompt: '请为{topic}创作一篇通用文案，包含以下关键词：{keywords}，字数要求：{length}' },
          ecommerce: { enabled: true, prompt: '请为{topic}创作一篇电商营销文案，包含以下关键词：{keywords}，字数要求：{length}' },
          project: { enabled: true, prompt: '请为{topic}创作一篇项目方案文案，包含以下关键词：{keywords}，字数要求：{length}' },
          improve: { enabled: true, prompt: '请为{topic}创作一篇文案改进方案，包含以下关键词：{keywords}，字数要求：{length}' },
          summary: { enabled: true, prompt: '请为{topic}创作一篇总结报告文案，包含以下关键词：{keywords}，字数要求：{length}' },
          styles: {
            professional: { name: '专业', description: '适合商务场合', enabled: true },
            casual: { name: '活泼', description: '适合日常推广', enabled: true }
          },
          lengthOptions: [
            { key: 'short', label: '短文案（50~100）', min: 50, max: 100 },
            { key: 'medium', label: '中文案（100~500）', min: 100, max: 500 },
            { key: 'long', label: '长文案（500以上）', min: 500, max: 9999 }
          ],
          model: 'gpt-3.5-turbo',
          temperature: 0.7,
          maxTokens: 1000
        }
        this.selectedStyles = ['professional', 'casual']
        this.selectedLengthOptions = ['short', 'medium', 'long']
        this.$message.info('已重置')
      }).catch(() => {})
    },
    openAddStyleDialog() {
      this.newStyle = { name: '', description: '', enabled: true }
      this.addStyleDialogVisible = true
    },
    addStyle() {
      if (!this.newStyle.name || !this.newStyle.name.trim()) {
        this.$message.warning('请输入风格名称')
        return
      }
      const key = this.newStyle.name.toLowerCase().replace(/\s+/g, '_')
      this.$set(this.config.styles, key, {
        name: this.newStyle.name,
        description: this.newStyle.description || '',
        enabled: this.newStyle.enabled
      })
      // 如果新风格默认启用，则添加到选中列表
      if (this.newStyle.enabled && !this.selectedStyles.includes(key)) {
        this.selectedStyles.push(key)
      }
      this.$message.success('添加成功')
      this.addStyleDialogVisible = false
    },
    removeStyle(key) {
      this.$delete(this.config.styles, key)
      this.selectedStyles = this.selectedStyles.filter(s => s !== key)
      this.$message.info('已删除')
    }
  },
  mounted() {
    this.loadConfig()
    this.loadModels()
  }
}
</script>

<style scoped>
.text-config-container {
  padding: 20px 0;
}

.text-config-content {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: #333;
}

.text-config-header {
  margin-bottom: 30px;
}

.text-config-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.text-config-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.config-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.config-section:last-of-type {
  border-bottom: none;
}

.config-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.config-item {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
}

.config-item label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}

.prompt-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 8px;
  font-size: 14px;
  box-sizing: border-box;
}

.style-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  align-items: center;
}

.style-tag {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 16px;
  background: #f9f9f9;
  font-size: 14px;
  transition: all 0.3s ease;
}

.style-tag.enabled {
  background: #f0f9ff;
  border-color: #d9ecff;
}

.style-name {
  font-weight: 500;
  color: #333;
}

.style-desc {
  color: #666;
  font-size: 12px;
}

.toggle-btn {
  font-size: 14px;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn {
  font-size: 12px;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
}

.remove-btn:hover {
  color: #f56c6c;
}

.add-style-btn {
  border-radius: 16px;
  font-size: 14px;
  padding: 6px 16px;
}

.length-config {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.length-checkbox {
  margin-right: 20px;
}

.model-config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.number-input {
  width: 120px;
}

.unit {
  margin-left: 8px;
  color: #666;
}

.select {
  width: 100%;
}

.model-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.model-icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
}

.model-name {
  flex: 1;
}

.model-tag {
  margin-left: 4px;
}

.actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}
</style>
